import {
  Box,
  Button,
  Center,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Text,
  useUpdateEffect,
} from '@chakra-ui/react';
import { Form, Formik, FormikHelpers } from 'formik';
import useVerifyOtp from 'hooks/modules/otp/useVerifyOtp';
import useFormError from 'hooks/shared/useFormError';
import useToastError from 'hooks/shared/useToastError';
import { OtpId } from 'model/modules/otp/types';
import { FC } from 'react';
import Countdown from 'react-countdown';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { ButtonVariant } from 'presentation/theme/components/button';
import FormNumberInput from 'presentation/components/shared/form/FormNumberInput';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string | null;
  onSubmit: (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ) => Promise<void>;
  otpVerifying: boolean;
  onSendNewCode: () => Promise<OtpId>;
  newCodeRequesting: boolean;
  title: string;
  text: string;
}

interface FormValues {
  code: string;
  otpId: OtpId | null;
}

const PhoneConfirmationModal: FC<Props> = ({
  isOpen,
  onClose,
  phoneNumber,
  onSubmit,
  otpVerifying,
  onSendNewCode,
  newCodeRequesting,
  title,
  text,
}) => {
  const { t } = useTranslation();

  const { sendNewCodeDate, updateOtpId, lastOtpId } = useVerifyOtp<OtpId>();

  const handleFormError = useFormError<FormValues>();

  const sendNewCode = (): void => {
    onSendNewCode()
      .then((otpId) => updateOtpId(otpId))
      .catch(toastErrorHandler);
  };

  useUpdateEffect(() => {
    if (isOpen) {
      sendNewCode();
    }
  }, [isOpen]);

  const toastErrorHandler = useToastError();

  const validationSchema = Yup.object().shape({
    code: Yup.string().required(t('fieldIsRequired') ?? undefined),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent h="290px" w="445px">
        <ModalHeader mt="20px" mx="30px" textAlign="center">{title}</ModalHeader>
        <ModalBody>
          <Flex mb="23px" textAlign="center" justifyContent="center" flex="center">{text} {phoneNumber}</Flex>
          <Formik<FormValues>
            validationSchema={validationSchema}
            initialValues={{
              code: '',
              otpId: null,
            }}
            onSubmit={(values, formik) => {
              onSubmit(
                {
                  code: values.code,
                  otpId: lastOtpId,
                },
                formik
              ).catch((err) => {
                handleFormError(err, formik);
              });
            }}
          >
            {({ values, setFieldValue }) => {
              return (
                <Form>
                  <Flex
                    alignItems="flex-start"
                    flexDirection={{ base: 'column', md: 'row' }}
                    justifyContent="space-between"
                  >
                    <Box flex="1" w={{ base: 'full', md: 'unset' }}>
                      <FormNumberInput
                        name="code"
                        value={values.code}
                        onChange={(val) => {
                          if (val.length > 6) {
                            val = val.substring(0, 6);
                          }
                          setFieldValue('code', val);
                        }}
                        isDisabled={otpVerifying}
                      />
                    </Box>
                    <Button
                      w={{ base: 'full', md: 'unset' }}
                      ml={{ base: '0', md: '20px' }}
                      mt={{ base: '20px', md: '0' }}
                      type="submit"
                      isLoading={otpVerifying}
                    >
                      {t('continuePhrase')}
                    </Button>
                  </Flex>
                </Form>
              );
            }}
          </Formik>
          <Spacer h="20px"/>
          {phoneNumber && (
            <Center h={10}>
              {newCodeRequesting ? (
                <Spinner />
              ) : (
                <Countdown
                  key={sendNewCodeDate.getTime()}
                  date={sendNewCodeDate}
                  renderer={({ seconds, completed }) => {
                    if (completed) {
                      return (
                        <Button
                          variant={ButtonVariant.Link}
                          onClick={sendNewCode}
                        >
                          {t('requestCodeAgain')}
                        </Button>
                      );
                    } else {
                      return (
                        <Text>{t('requestCodeIn', { time: seconds })}</Text>
                      );
                    }
                  }}
                />
              )}
            </Center>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PhoneConfirmationModal;
